import { Megaphone, Sparkles, Timer, Users } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/utils/cn";
import type { ChatChannel, ChatConversationType } from "@/types/(my-space)/chat.interface";

const SIZES = {
  xs: "size-6 text-[10px]",
  sm: "size-8 text-xs",
  md: "size-10 text-sm",
  lg: "size-12 text-base",
  xl: "size-20 text-2xl",
} as const;

const DOT_SIZES = {
  xs: "size-2",
  sm: "size-2.5",
  md: "size-3",
  lg: "size-3.5",
  xl: "size-4",
} as const;

/** "(You)" on a self chat's title is a tag, not part of the name. */
const initialsOf = (name: string) =>
  name
    .replace(/\([^)]*\)/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

interface ChatAvatarProps {
  name: string;
  imageUrl?: string | null;
  /** Group/broadcast without a picture, and app channels, show their kind's icon. */
  kind?: ChatConversationType;
  /** Which app channel (kind "channel"): MaiHRMS shows its logo. */
  channel?: ChatChannel;
  size?: keyof typeof SIZES;
  isOnline?: boolean;
  className?: string;
}

/** A person or conversation picture, with an optional online dot. */
export const ChatAvatar = ({
  name,
  imageUrl,
  kind = "direct",
  channel,
  size = "md",
  isOnline = false,
  className,
}: ChatAvatarProps) => {
  const isChannel = kind === "channel";
  const official = isChannel && channel === "product_updates";
  // An app channel shows its own artwork: MaiHRMS's logo, or Time &
  // Attendance's stopwatch (also for an older channel row with no `channel`).
  const picture = !isChannel
    ? imageUrl
    : official
      ? "/icons/app-icon.svg"
      : "/icons/interface-icons/shared/icon-3d-stopwatch.png";
  const KindIcon =
    kind === "broadcast"
      ? Megaphone
      : kind === "group"
        ? Users
        : kind === "channel"
          ? official
            ? Sparkles
            : Timer
          : null;
  return (
    <span className={cn("relative inline-flex shrink-0", className)}>
      <Avatar className={cn("rounded-full", SIZES[size], isChannel && "bg-white ring-1 ring-border")}>
        {picture ? (
          <AvatarImage
            src={picture}
            alt=""
            className={official ? "object-contain p-[15%]" : isChannel ? "object-contain p-[8%]" : undefined}
          />
        ) : null}
        <AvatarFallback
          className={cn(
            "rounded-full font-semibold",
            // An app channel is the app's own voice: a solid badge.
            kind === "channel"
              ? "bg-info text-info-foreground"
              : KindIcon
                ? "bg-primary-muted text-primary"
                : "bg-info-muted text-info",
          )}
        >
          {KindIcon ? <KindIcon className="size-1/2" aria-hidden /> : initialsOf(name)}
        </AvatarFallback>
      </Avatar>
      {isOnline ? (
        <span
          className={cn(
            "absolute bottom-0 end-0 rounded-full bg-success ring-2 ring-background",
            DOT_SIZES[size],
          )}
        />
      ) : null}
    </span>
  );
};
